import { get2faToken, getToken } from '../utils/helpers';
import AppConfig from './AppConfig';

export async function apiFetch<T = null>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
	// -------------------------
	// 🔹 Resolve input to string URL
	// -------------------------
	let url = input instanceof URL ? input.toString() : input.toString();

	// Append debug query param in dev mode
	if (process.env.NODE_ENV === 'development' && !url.includes('fineract-provider/api/v1')) {
		const hasQuery = url.includes('?');
		url += `${hasQuery ? '&' : '?'}debug=true`;
	}

	// -------------------------
	// 🔹 Merge headers (no override)
	// -------------------------
	const baseHeaders = await getAPIHeaders();
	const initHeaders = Object.fromEntries(
		Object.entries(init?.headers ?? ({} as Record<string, string>)),
	);

	// Merge without overriding existing user-supplied keys
	const mergedHeaders = { ...baseHeaders, ...initHeaders };

	// -------------------------
	// 🔹 Perform fetch
	// -------------------------
	const response = await fetch(url, {
		...init,
		headers: mergedHeaders,
	});

	// -------------------------
	// 🔹 Handle response
	// -------------------------
	return handleResponse<T>(response);
}

export type APIResponseData = Record<string, any> | string | Blob | null;

// ---------- handleResponse ----------
async function handleResponse<T = APIResponseData>(response: Response): Promise<T> {
	try {
		let data: any;
		const ctype = response.headers.get('content-type')?.toLowerCase();

		if (ctype?.includes('application/json')) {
			data = await response.json();
		} else if (ctype?.includes('text/')) {
			data = await response.text();
		} else if (
			ctype?.includes('application/pdf') ||
			ctype?.includes('text/csv') ||
			ctype?.includes('application/octet-stream') ||
			ctype?.startsWith('image/') ||
			ctype?.startsWith('application/')
		) {
			data = await response.blob();
		} else {
			data = await response.text();
		}

		// Handle 401 Unauthorized
		if (response.status === 401) {
			if (typeof (window as any).logout === 'function') {
				(window as any).logout();
			}
			return Promise.reject('Unauthorized: Please log in again.');
		}

		if (!response.ok) {
			return Promise.reject(cleanApiError(data));
		}

		return Promise.resolve(data as T);
	} catch {
		return Promise.reject('invalid response was received');
	}
}

// ---------- cleanApiError ----------
function cleanApiError(error: any): string {
	if (error?.errors?.length > 0 && error.errors[0].defaultUserMessage) {
		return error.errors[0].defaultUserMessage;
	}
	if (error?.defaultUserMessage) return error.defaultUserMessage;
	if (error?.error) return error.error;
	if (error?.message) return error.message;
	if (typeof error === 'string') return error.substring(0, 100);
	return 'network error';
}

async function getAPIHeaders(
	defaultHeaders?: Record<string, string>,
): Promise<Record<string, string | number | boolean>> {
	try {
		const headers = await getAuthAPIHeaders(defaultHeaders);
		const mfaToken = await get2faToken();

		if (mfaToken) {
			headers['fineract-platform-tfa-token'] = mfaToken;
		}

		return Promise.resolve(headers);
	} catch {
		return Promise.resolve(defaultHeaders ?? {});
	}
}

export async function getAuthAPIHeaders(
	defaultHeaders?: Record<string, string>,
): Promise<Record<string, string | number | boolean>> {
	try {
		const headers = defaultHeaders ?? {};
		const token = await getToken();
		headers['Authorization'] = `Basic ${token}`;
		headers['Fineract-Platform-Tenantid'] = AppConfig.tenantId;
		return Promise.resolve(headers);
	} catch {
		return Promise.resolve(defaultHeaders ?? {});
	}
}
